import { zodResolver } from "@hookform/resolvers/zod";
import { Box } from "@mui/material";
import { extractErrorMessage, useInvalidateQuery } from "@platform/api";
import {
  addCaseToGroupMutation,
  removeCaseFromGroupMutation,
  type TestGroupOut,
} from "@platform/api/golden";
import {
  Alert,
  Button,
  Card,
  Chip,
  Link,
  TextField,
  Typography,
} from "@platform/design-system";
import { useMutation } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { Link as RouterLink } from "react-router";
import { z } from "zod";

import { useGoldenClient } from "../api/GoldenClientProvider";
import { GOLDEN_TAGS } from "../api/goldenTags";

import type { ReactNode } from "react";

export const TEST_GROUP_TEST_ID = "golden-test-group";

const addCaseSchema = z.object({
  caseId: z
    .string()
    .trim()
    .regex(/^#?[1-9][0-9]*$/, "Enter a case id, for example 12")
    .transform((value) => Number(value.replace(/^#/, ""))),
});

type AddCaseInput = z.input<typeof addCaseSchema>;
type AddCaseValues = z.output<typeof addCaseSchema>;

/** One test group: its cases, a form to add one, and the action that freezes it as a release. */
export const TestGroupCard = ({
  group,
  onFreeze,
  freezing,
}: {
  readonly group: TestGroupOut;
  readonly onFreeze: (groupName: string) => void;
  readonly freezing: boolean;
}): ReactNode => {
  const client = useGoldenClient();
  const invalidateQuery = useInvalidateQuery();
  const testId = `${TEST_GROUP_TEST_ID}-${group.name}`;
  const refreshGroups = () =>
    invalidateQuery({ tags: [...GOLDEN_TAGS.testGroups] });
  const { control, handleSubmit, reset } = useForm<
    AddCaseInput,
    unknown,
    AddCaseValues
  >({
    resolver: zodResolver(addCaseSchema),
    defaultValues: { caseId: "" },
  });
  const addCase = useMutation({
    ...addCaseToGroupMutation({ client }),
    onSuccess: async () => {
      reset();
      await refreshGroups();
    },
  });
  const removeCase = useMutation({
    ...removeCaseFromGroupMutation({ client }),
    onSuccess: refreshGroups,
  });
  const failure = addCase.error ?? removeCase.error;
  const caseIds = group.case_ids ?? [];

  const submit = handleSubmit(({ caseId }) =>
    addCase.mutate({ path: { name: group.name, case_id: caseId } }),
  );

  return (
    <Card data-testid={testId}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "baseline",
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="header-small"
            component="h2"
            sx={{ fontFamily: "monospace" }}
          >
            {group.name}
          </Typography>
          {group.description && (
            <Typography variant="body-medium" color="textSecondary">
              {group.description}
            </Typography>
          )}
        </Box>
        {failure && (
          <Alert data-testid={`${testId}-error`} severity="error">
            {extractErrorMessage(failure, "The action failed")}
          </Alert>
        )}
        <Box
          data-testid={`${testId}-cases`}
          sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
        >
          {caseIds.map((caseId) => (
            <Chip
              key={caseId}
              data-testid={`${testId}-case-${caseId}`}
              variant="outlined"
              leadingVisual={false}
              label={
                <Link component={RouterLink} to={`/cases/${caseId}`}>
                  #{caseId}
                </Link>
              }
              action
              onDelete={() =>
                removeCase.mutate({
                  path: { name: group.name, case_id: caseId },
                })
              }
            />
          ))}
          {caseIds.length === 0 && (
            <Typography variant="body-medium" color="textSecondary">
              No cases.
            </Typography>
          )}
        </Box>
        <Box
          component="form"
          noValidate
          onSubmit={(event) => void submit(event)}
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          {/* The design-system field forwards its ref to the wrapper, not the input,
              so it is controlled rather than registered. */}
          <Controller
            control={control}
            name="caseId"
            render={({ field: { ref: _ref, ...field }, fieldState }) => (
              <TextField
                data-testid={`${testId}-case-input`}
                label="Case id"
                slotProps={{ htmlInput: { inputMode: "numeric" } }}
                error={fieldState.error !== undefined}
                helperText={fieldState.error?.message}
                {...field}
              />
            )}
          />
          <Button
            type="submit"
            variant="secondary"
            data-testid={`${testId}-add`}
            loading={addCase.isPending}
          >
            Add case
          </Button>
          <Button
            data-testid={`${testId}-freeze`}
            loading={freezing}
            onClick={() => onFreeze(group.name)}
          >
            Freeze as release
          </Button>
        </Box>
      </Box>
    </Card>
  );
};
